// RegisterPage — 회원가입(이메일 인증 발송).
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api/client';
import useIsMobile from '../hooks/useIsMobile';
import { COLORS } from '../utils/uiStyles';

const PASSWORD_MIN_LENGTH = 8;

export default function RegisterPage() {
  const [form, setForm] = useState({ email:'', password:'', name:'' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const S = styles(mobile);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    if (form.password.length < PASSWORD_MIN_LENGTH) {
      setError(`비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상 입력해 주세요.`);
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.register(form);
      setMessage(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || '가입에 실패했습니다.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (loading) return;
    setError(''); setLoading(true);
    try {
      const res = await authAPI.register(form);
      setMessage(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || '인증 메일 재요청에 실패했습니다.');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <h1 style={S.title}>회원가입</h1>
        <p style={S.desc}>비밀번호는 최소 {PASSWORD_MIN_LENGTH}자 이상 입력해야 합니다. 이메일 인증 후 관리자 승인을 거쳐 사용할 수 있습니다.</p>

        {message ? (
          <div style={S.panelOk} role="status">
            <p style={S.ok}>{message}</p>
            <ol style={S.steps}>
              <li>입력한 이메일의 인증 링크를 열어 주세요.</li>
              <li>이메일 인증 후 관리자 승인 대기 상태가 됩니다.</li>
              <li>승인 완료 후 로그인할 수 있습니다.</li>
            </ol>
            <button type="button" style={{ ...S.secondaryBtn, ...(loading ? S.secondaryBtnDisabled : {}) }} onClick={handleResend} disabled={loading}>
              {loading ? '재요청 중...' : '인증 메일 다시 보내기'}
            </button>
            <button style={S.btn} onClick={() => navigate('/login')}>로그인 화면으로</button>
          </div>

        ) : (
          <form onSubmit={handleSubmit} style={S.form}>
            <label style={S.label}>이름</label>
            <input style={S.input} type="text" placeholder="이름" autoComplete="name"
              value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} required />
            <label style={S.label}>이메일</label>
            <input style={S.input} type="email" placeholder="name@ourbox.co.kr" autoComplete="email" spellCheck={false}
              value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} required />
            <label style={S.label}>비밀번호</label>
            <input style={S.input} type="password" placeholder={`${PASSWORD_MIN_LENGTH}자 이상 입력`} autoComplete="new-password" aria-describedby="register-password-help"
              value={form.password} onChange={(e) => setForm({...form, password:e.target.value})} required />
            <p id="register-password-help" style={S.helpText}>보안을 위해 비밀번호는 최소 {PASSWORD_MIN_LENGTH}자 이상으로 설정해 주세요.</p>
            {error && <p style={S.err} role="alert">{error}</p>}
            <button style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }} type="submit" disabled={loading}>
              {loading ? '처리 중...' : '가입하기'}
            </button>
          </form>
        )}

        <p style={S.link}><Link to="/login">로그인으로 돌아가기</Link></p>
      </div>
    </div>
  );
}

const styles = (mobile) => ({
  wrap: { display:'flex', justifyContent:'center', alignItems:'center', minHeight:'var(--gw-app-h, 100dvh)', background:'#f5f5f5', boxSizing:'border-box', padding: mobile ? '16px' : 0 },
  card: { background:'white', padding: mobile ? '28px 20px' : '40px', borderRadius:'12px', boxShadow: mobile ? 'none' : '0 2px 12px rgba(0,0,0,.1)', width: mobile ? '100%' : '360px', maxWidth: mobile ? '420px' : undefined, boxSizing:'border-box' },
  title: { textAlign:'center', marginBottom:'8px', fontSize: mobile ? '20px' : '22px', fontWeight:'700' },
  desc: { margin:'0 0 22px', color:'#6b7280', fontSize:'13px', textAlign:'center', lineHeight:1.5 },
  form: { display:'flex', flexDirection:'column' },
  label: { fontSize:'12px', color:'#4b5563', fontWeight:700, marginBottom:'6px' },
  input: { width:'100%', padding: mobile ? '13px 14px' : '10px 12px', marginBottom:'12px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize: mobile ? '16px' : '14px', boxSizing:'border-box', minHeight: mobile ? '46px' : '40px', outline:'none' },
  helpText: { margin:'-6px 0 12px', color:'#6b7280', fontSize:'12px', lineHeight:1.4 },
  btn: { width:'100%', padding: mobile ? '14px' : '12px', minHeight: mobile ? '48px' : '42px', background:COLORS.primary, color:'white', border:'none', borderRadius:'6px', fontSize:'15px', fontWeight:700, cursor:'pointer' },
  btnDisabled: { background:COLORS.border, color:COLORS.textMuted, cursor:'not-allowed' },
  secondaryBtn: { width:'100%', padding: mobile ? '12px' : '10px', minHeight: mobile ? '44px' : '38px', marginBottom:'10px', background:'white', color:COLORS.info, border:`1px solid ${COLORS.primaryBorder}`, borderRadius:'6px', fontSize:'14px', fontWeight:700, cursor:'pointer' },
  secondaryBtnDisabled: { color:'#9ca3af', borderColor:'#e5e7eb', cursor:'not-allowed' },
  err: { color:'#b91c1c', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'8px', padding:'10px 12px', fontSize:'13px', margin:'0 0 12px', lineHeight:1.5 },
  panelOk: { background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'10px', padding:'14px', marginBottom:'14px' },
  ok: { color:'#166534', fontSize:'14px', margin:'0 0 14px', textAlign:'center', lineHeight:1.6 },
  steps: { margin:'0 0 14px', paddingLeft:'20px', color:'#166534', fontSize:'13px', lineHeight:1.7 },
  link: { textAlign:'center', marginTop:'16px', fontSize:'13px' },
});
